import { useSearchParams } from "react-router";
import { Trans } from "@lingui/react/macro";
import React from "react";
import { classNames } from "~/utils";
import MindstoneLogoPurple from "../icons/mindstone-logo-purple";
import { LogoPurple } from "../icons";
import { Separator } from "@mindstonehq/ui";
import { VideoCameraIcon } from "@heroicons/react/24/outline";

export default function LeftHandSide() {
  const [searchParams] = useSearchParams();
  const isOnlineEventRedirect = searchParams.get("onlineEventRedirect") === "true";

  return (
    <>
      {isOnlineEventRedirect ? (
        <div className={classNames("overflow-visible min-h-screen h-full w-full bg-quaternary")}>
          <div className="h-full w-full relative overflow-hidden">
            <Shape2 className="absolute w-[105%] inset-0 flex items-center justify-center text-quaternary-hover" />
            <Ellipse1 className="absolute p-16 w-full inset-0 flex items-center justify-center h-screen" />
            <div className="absolute inset-0 flex items-center justify-center text-center min-h-screen">
              <div className="flex flex-col items-center justify-center text-white max-w-sm">
                <h2 className="text-accent mb-3 px-4 flex items-center gap-6">
                  <VideoCameraIcon className="w-10 h-10 text-muted-foreground" />
                  <Separator orientation="vertical" className="w-1 h-8" />
                  <MindstoneLogoPurple />
                </h2>
                <p className="text-muted-foreground text-lg font-bold"><Trans>Ready for your live session?</Trans></p>
                <p className="text-muted-foreground text-sm w-72">
                  <Trans>Simply fill out a few details so we can connect you to your session.</Trans>
                </p>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div
          className={classNames("overflow-visible min-h-screen h-full w-full relative")}
          style={{ backgroundColor: "#EEF2FF" }}
        >
          {/* Background image layer */}
          <img
            src={"https://storage.googleapis.com/mindstone-public-assets/signup/background%20image.jpg"}
            alt=""
            className="absolute inset-0 w-full h-full object-cover select-none pointer-events-none"
          />
          {/* Inside shape */}
          <div className="absolute inset-0 flex items-center justify-center">
            <InsideShape className="w-[110%] max-w-none" />
          </div>
          {/* Logo */}
          <div className="absolute inset-0 flex items-center justify-center">
            <LogoPurple className="w-[280px]" />
          </div>
        </div>
      )}
    </>
  );
}

const Shape2 = (props: React.ComponentProps<"svg">) => {
  return (
    <svg viewBox="0 0 815 775" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M727.078 248.564C758.857 259.657 785.194 284.281 796.908 318.176C807.913 350.021 820.145 395.415 822.049 444.032C823.882 490.829 816.703 558.303 768.813 615.188C722.975 669.637 662.926 731.096 565.18 760.024C484.626 783.863 407.033 773.249 355.616 766.216C354.529 766.067 353.453 765.92 352.389 765.775C283.129 756.322 195.972 734.676 129.806 655.723C107.897 629.579 77.3989 592.961 52.4829 551.589C27.7972 510.6 2.78958 455.751 1.03761 391.902C-1.11276 313.535 20.1337 239.271 59.0887 173.677C90.5222 120.747 144.361 90.3973 164.25 79.1858C166.338 78.0084 168.052 77.0422 169.327 76.283C173.669 73.6959 178.777 70.5267 184.528 66.958C210.593 50.7847 249.881 26.4061 291.155 10.8201C326.035 -2.35111 369.036 -13.8384 414.167 -17.6264C458.308 -21.3313 513.504 -18.3957 565.315 6.49476C615.469 30.5897 650.2 71.2466 672.453 105.814C695.511 141.63 712.134 181.688 720.973 218.336C723.407 228.43 725.429 238.514 727.078 248.564Z"
        fill="currentColor"
      />
    </svg>
  );
};

const Ellipse1 = (props: React.ComponentProps<"svg">) => {
  return (
    <svg width="653" height="664" viewBox="0 0 653 664" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M174.08 41.6482C329.893 -40.0493 524.386 23.7572 608.492 184.164C692.598 344.571 634.468 540.835 478.654 622.533C322.841 704.23 128.348 640.424 44.2419 480.017C-39.864 319.61 18.2664 123.346 174.08 41.6482ZM171.694 37.0982C329.949 -45.8792 527.489 18.9269 612.912 181.847C698.336 344.766 639.294 544.105 481.04 627.083C322.786 710.06 125.246 645.254 39.8222 482.334C-45.6013 319.415 13.4398 120.076 171.694 37.0982Z"
        fill="white"
      />
    </svg>
  );
};

// Shapes for the Figma design
const CircleShape = (props: React.ComponentProps<"svg">) => {
  return (
    <svg viewBox="0 0 800 800" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-[90%] h-auto" {...props}>
      <circle cx="400" cy="400" r="350" fill="currentColor" />
    </svg>
  );
};

const EllipseStroke = (props: React.ComponentProps<"svg">) => {
  return (
    <svg width="653" height="664" viewBox="0 0 653 664" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M174.08 41.6482C329.893 -40.0493 524.386 23.7572 608.492 184.164C692.598 344.571 634.468 540.835 478.654 622.533C322.841 704.23 128.348 640.424 44.2419 480.017C-39.864 319.61 18.2664 123.346 174.08 41.6482ZM171.694 37.0982C329.949 -45.8792 527.489 18.9269 612.912 181.847C698.336 344.766 639.294 544.105 481.04 627.083C322.786 710.06 125.246 645.254 39.8222 482.334C-45.6013 319.415 13.4398 120.076 171.694 37.0982Z"
        fill="white"
        opacity="0.5"
      />
    </svg>
  );
};

// Inside shape from design
const InsideShape = (props: React.ComponentProps<"svg">) => {
  return (
    <svg viewBox="0 0 720 800" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
      <g opacity="0.3" filter="url(#filter0_d_16018_5601)">
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M266.664 86.5715C281.1 57.6373 307.77 35.535 341.496 28.4958C373.18 21.8827 417.927 15.7481 464.488 19.8477C509.305 23.7936 572.732 38.8428 621.114 91.3992C667.424 141.705 718.687 206.404 734.363 303.07C747.281 382.733 727.727 455.384 714.77 503.526C714.496 504.544 714.225 505.551 713.957 506.547C696.524 571.399 665.294 651.821 582.007 705.269C554.428 722.967 515.823 747.576 473.367 766.286C431.304 784.824 375.994 801.983 314.936 795.885C239.995 788.401 171.81 759.12 114.041 714.019C67.4254 677.626 45.0532 622.629 36.7886 602.312C35.9208 600.178 35.2086 598.427 34.6401 597.121C32.703 592.667 30.3042 587.415 27.6031 581.5C15.3616 554.694 -3.09052 514.289 -12.9222 473.061C-21.2307 438.221 -26.9463 395.845 -25.066 352.377C-23.2269 309.862 -13.7149 257.621 16.307 211.276C45.3694 166.413 88.3382 138.262 123.986 121.261C160.922 103.645 201.117 92.6772 237.116 88.7125C247.031 87.6205 256.887 86.92 266.664 86.5715Z"
          fill="#EEF2FF"
        />
      </g>
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M229.758 138.489C376.071 61.773 558.705 121.689 637.682 272.315C716.66 422.941 662.074 607.238 515.761 683.954C369.449 760.67 186.815 700.754 107.837 550.128C28.8597 399.502 83.4456 215.205 229.758 138.489ZM227.518 134.216C376.123 56.2987 561.618 117.153 641.833 270.139C722.047 423.124 666.606 610.309 518.001 688.226C369.397 766.144 183.902 705.29 103.687 552.304C23.4722 399.318 78.9133 212.134 227.518 134.216Z"
        fill="white"
      />
      <defs>
        <filter
          id="filter0_d_16018_5601"
          x="-55.4253"
          y="-11.4763"
          width="824.023"
          height="838.615"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feColorMatrix
            in="SourceAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />
          <feOffset />
          <feGaussianBlur stdDeviation="15" />
          <feComposite in2="hardAlpha" operator="out" />
          <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.2 0" />
          <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_16018_5601" />
          <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_16018_5601" result="shape" />
        </filter>
      </defs>
    </svg>
  );
};
